import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import TypewriterComponent from "typewriter-effect";
import CalendlyLink from "../global/CalendlyLink";
import EmailLink from "../global/EmailLink";
import WhatsAppLink from "../global/WhatsAppLink";
import { CTA_IDS, CTA_LOCATIONS } from "../../lib/analytics.constants";

const Footer = forwardRef((props, ref) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer ref={ref} className="w-full flex flex-col justify-start items-center border-t border-stone-200 dark:border-white/[0.08] overflow-hidden bg-white dark:bg-[#0a0a0a]">
      <div className="w-full flex flex-col justify-start items-center bg-transparent">
        <div className="w-full max-w-6xl px-6">
          {/* Main Footer Content */}
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-10 pt-12 pb-10">
            {/* Column 1: CTA */}
            <div className="flex flex-col justify-start items-start">
              <p className="text-2xl md:text-3xl font-bold mb-4 dark:text-white">
                ¿Trabajamos juntos?
              </p>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Contanos tu idea, te armamos roadmap, costo y plan de pagos.
              </p>
              <CalendlyLink
                ctaId={CTA_IDS.FOOTER_CALENDLY_PRIMARY}
                ctaLocation={CTA_LOCATIONS.FOOTER}
                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-semibold px-6 py-3 rounded-full bg-[#11b36d] text-white hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
              >
                Armar mi plan
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </CalendlyLink>
            </div>

            {/* Column 2: Navigation */}
            <div className="flex flex-col justify-start items-start md:items-center">
              <h4 className="font-bold text-[#0b2b40] dark:text-white mb-4">Navegación</h4>
              <div className="flex flex-col gap-2">
                <Link to="/" className="text-gray-600 dark:text-gray-300 hover:text-[#11b36d] transition-colors">
                  Inicio
                </Link>
                <Link to="/modelos" className="text-gray-600 dark:text-gray-300 hover:text-[#11b36d] transition-colors">
                  Modelos de trabajo
                </Link>
                <Link to="/conocenos" className="text-gray-600 dark:text-gray-300 hover:text-[#11b36d] transition-colors">
                  Conócenos
                </Link>
                <Link to="/capacidades" className="text-gray-600 dark:text-gray-300 hover:text-[#11b36d] transition-colors">
                  Capacidades
                </Link>
                <CalendlyLink
                  ctaId={CTA_IDS.FOOTER_CALENDLY_NAV}
                  ctaLocation={CTA_LOCATIONS.FOOTER}
                  className="text-gray-600 dark:text-gray-300 hover:text-[#11b36d] transition-colors"
                >
                  Agendar
                </CalendlyLink>
              </div>
            </div>

            {/* Column 3: Contact & Social */}
            <div className="flex flex-col justify-start items-start md:items-end">
              <h4 className="font-bold text-[#0b2b40] dark:text-white mb-4">Contacto</h4>
              <div className="flex flex-col gap-3 items-start md:items-end">
                <EmailLink
                  email="hola@eientech.io"
                  ctaId={CTA_IDS.FOOTER_EMAIL}
                  ctaLocation={CTA_LOCATIONS.FOOTER}
                  className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-[#11b36d] transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                  hola@eientech.io
                </EmailLink>
                <WhatsAppLink
                  ctaId={CTA_IDS.FOOTER_WHATSAPP}
                  ctaLocation={CTA_LOCATIONS.FOOTER}
                  message="¡Hola EiEn, quiero saber más!"
                  className="inline-flex items-center gap-2 text-[#11b36d] font-semibold hover:text-green-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </WhatsAppLink>
                <div className="flex gap-4 mt-2">
                  <a
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-gray-400 hover:text-[#E1306C] transition-colors"
                    href="https://www.instagram.com/eien.dev/"
                    aria-label="Instagram"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-gray-400 hover:text-[#0077B5] transition-colors"
                    href="https://linkedin.com/company/eien-innovations-tech/about/"
                    aria-label="LinkedIn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-gray-400 hover:text-[#FF0000] transition-colors"
                    href="https://www.youtube.com/@EiEnInnovationsTech"
                    aria-label="YouTube"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Tagline */}
      <div className="w-full py-8 border-t border-stone-100 dark:border-white/[0.06]">
        <div className="text-2xl md:text-4xl font-semibold text-center px-4 dark:text-white">
            <TypewriterComponent
            options={{
              strings: [
                '<span class="text-gradient text-short text-color ignore-translation">eien</span>',
                "Somos el aliado tecnológico detrás de grandes ideas",
                "Plataformas · Apps · SaaS",
                "Proyecto · Partnership · Revenue Share",
              ],
              autoStart: true,
              loop: true,
              delay: 80,
              deleteSpeed: 30,
            }}
          />
        </div>
      </div>

      {/* SEO tagline */}
      <div className="w-full py-3 border-t border-stone-100 dark:border-white/[0.06]">
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 px-4">
          <span className="ignore-translation">eien</span> — Empresa de desarrollo de software a medida en Paraguay, Asunción. Desarrollamos aplicaciones web, apps móviles, SaaS, sistemas de gestión empresarial, automatización de procesos y digitalización para empresas y startups de Paraguay y LATAM.
        </p>
      </div>

      {/* Copyright */}
      <div className="w-full py-4 bg-gray-50 dark:bg-[#111111] border-t border-stone-100 dark:border-white/[0.06]">
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          © {currentYear} <span className="ignore-translation">eien</span> — Todos los derechos reservados.
        </p>
      </div>
    </footer>
  );
});

export default Footer;
